import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { consumeOauthHandoff } from "@/lib/pwaOauthHandoff";
import { getActiveApp, getAppBySlug } from "@/lib/pwaAppConfig";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/", { replace: true });
      return;
    }
    const sessionId = m[1];

    // Recover the app + flow_id the login was started FOR. The handoff
    // is a diagnostic complement to the server's signed OAuth state; the
    // signed state is still the security boundary.
    const handoff = consumeOauthHandoff();
    const targetApp =
      (handoff?.app && getAppBySlug(handoff.app)) ||
      getActiveApp() ||
      getAppBySlug("user");
    const appSlug = targetApp?.identity?.id || "user";

    (async () => {
      try {
        // Backend requires the `app` field to bind the session to the
        // correct cookie name + role (Phase B.3 issue_app_session).
        const res = await api.post("/auth/session", {
          session_id: sessionId,
          app: appSlug,
          flow_id: handoff?.flow_id || null,
        });
        setUser(res.data.user);
        // Registry-driven redirect: land on THIS app's dashboard, not
        // a hardcoded /dashboard that only fits the User PWA. `replace`
        // also drops the `#session_id` hash — no raw history mutation
        // behind the router's back (that desyncs its location store and
        // surfaces as a "recoverable error during concurrent rendering").
        const dashboard = targetApp?.routing?.dashboard_route || "/dashboard";
        navigate(dashboard, { replace: true, state: { user: res.data.user } });
      } catch (_e) {
        // Fall the user back to that app's login page rather than /,
        // so lawyer/admin flows don't dump them on the public homepage.
        // Carry the backend's reason (e.g. "This account cannot sign into
        // the Advoset Lawyer app.") so the login page can explain honestly.
        const loginRoute = targetApp?.routing?.login_route || "/";
        const detail = _e?.response?.data?.detail;
        // Lawyer-app status gate: backend returns { code: "lawyer_gate",
        // gate: "unregistered" | "pending" | "rejected" } after Google
        // proved email ownership. The login page renders the exact state.
        const state =
          detail && typeof detail === "object" && detail.code === "lawyer_gate"
            ? { lawyer_gate: detail.gate }
            : typeof detail === "string"
              ? { auth_error: detail }
              : undefined;
        navigate(loginRoute, { replace: true, state });
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen grid place-items-center bg-[#FAFAFA]">
      <div className="text-center">
        <div className="h-10 w-10 mx-auto rounded-full border-4 border-slate-200 border-t-[#1E3A8A] animate-spin" />
        <p className="mt-4 text-sm text-slate-600 font-semibold">Signing you in…</p>
      </div>
    </div>
  );
}
